import axios from "axios";
import CryptoJS from "crypto-js";
import errorHandler from "Utils/error-handler";
import { setWithExpiry, getWithExpiry } from "Helpers/getset-with-expiry";
import { findIndex, reduce, omitBy, isEmpty, isNil, toLower } from "lodash";
import { getAccessToken } from "Helpers/session";
import { RiderType } from "Types/new_driver_types";
import { UserRoleToAssign } from "Types/user.types";

// Function to decrypt loginFile
function decryptLoginFile(loginFile: string, secretPassphrase: string) {
  const base64Words = CryptoJS.enc.Base64.parse(loginFile);
  const encoded = CryptoJS.enc.Utf8.stringify(base64Words).trim();
  const decrypted = CryptoJS.AES.decrypt(encoded, secretPassphrase).toString(
    CryptoJS.enc.Utf8,
  );

  return JSON.parse(decrypted);
}

export const authLogin = async (payload: {
  email: string;
  password: string;
  recaptchaToken: string;
}) => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/auth/login`;

  const lowerCaseEmail = toLower(payload.email);
  const emailHash = CryptoJS.SHA256(
    `${process.env.ACCOUNT_TYPE}:${lowerCaseEmail}`,
  ).toString(CryptoJS.enc.Hex);
  const passwordHash = CryptoJS.SHA256(payload.password).toString(
    CryptoJS.enc.Hex,
  );
  try {
    const response = await axios.post(url, {
      email: emailHash,
      password: passwordHash,
      recaptchaToken: payload.recaptchaToken,
    });

    let responseData = response.data;

    const decrypted = decryptLoginFile(responseData.loginFile, passwordHash);
    const loginSalt = decrypted.loginSalt;
    const loginHash = CryptoJS.SHA256(emailHash + loginSalt).toString(
      CryptoJS.enc.Hex,
    );

    const result = reduce(
      responseData.roles || [],
      (result: any[], role) => {
        if (role.status === "ACTIVE") {
          if (role.type === "HEALTH_PROVIDER") {
            role.type = "HEALTHCARE_PROVIDER";
          }

          const foundIndex = findIndex(
            result,
            (o: any) => o.orgID === role.orgID,
          );
          if (foundIndex < 0) {
            result.push({
              orgID: role.orgID,
              roleTypes: [`${role.type}-${role.subType}`],
            });
          } else {
            const roleTypes = result[foundIndex].roleTypes;
            roleTypes.push(`${role.type}-${role.subType}`);
            result[foundIndex] = { orgID: role.orgID, roleTypes };
          }
        }

        return result;
      },
      [],
    );

    const jwtData = await authJwt({
      email: lowerCaseEmail,
      loginHash: loginHash,
    });

    let org: any = {},
      selected: any = [];
    const accessToken = jwtData.accessToken;

    if (!isEmpty(result) && result.length === 1) {
      selected = result[0];
      org = await authGetOrgDetail(selected.orgID, accessToken);
    }

    // Combine response data from login API and response from JWT request API
    responseData = {
      ...responseData,
      ...jwtData,
      roleTypes: !isEmpty(selected) ? [selected.roleTypes[0]] : [], //ROLE TYPES IS ARRAY
      isRequireRoleTypesCheck: result.length !== 1, // check if org selection is needed
      org,
    };

    // Store the combined response data in localStorage as authentication passport
    setWithExpiry(process.env.APP_SESSION, responseData);

    return responseData;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, message, stack } = await errorHandler.response(
        error,
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const authJwt = async ({
  email,
  loginHash,
}: {
  email: string;
  loginHash: string;
}) => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/auth/jwt`;

  email = toLower(email);
  const currentDate = new Date();
  const currentDateInISOString = currentDate.toISOString();
  const emailHash = CryptoJS.SHA256(
    `${process.env.ACCOUNT_TYPE}:${email}`,
  ).toString(CryptoJS.enc.Hex);

  try {
    const response = await axios({
      method: "POST",
      url: url,
      data: {
        email: emailHash,
        time: currentDateInISOString,
        loginHash: loginHash,
      },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, message, stack } = await errorHandler.response(
        error,
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const authRefreshJwt = async () => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/auth/jwt/refresh`;
  const session = getWithExpiry(process.env.APP_SESSION);
  const parseSession = JSON.parse(session || "{}");

  try {
    const response = await axios({
      method: "POST",
      url: url,
      data: { refreshToken: parseSession.refreshToken },
    });

    let responseData = response.data;
    responseData = { ...parseSession, ...responseData };

    // Replace accessToken and refreshToken
    setWithExpiry(process.env.APP_SESSION, responseData);

    return true;
  } catch (err: any) {
    authRevokeJwtToken();
    if (window) window.location.href = window.location.href; // Force refresh client browser
    return false;
  }
};

export const authForgetPassword = async (payload: {
  email: string;
  recaptchaToken: string;
}) => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/auth/password/forgot`;

  const lowerCaseEmail = toLower(payload.email);
  const emailHash = CryptoJS.SHA256(
    `${process.env.ACCOUNT_TYPE}:${lowerCaseEmail}`,
  ).toString(CryptoJS.enc.Hex);

  try {
    const response = await axios({
      method: "POST",
      url: url,
      data: {
        email: emailHash,
        recaptchaToken: payload.recaptchaToken,
      },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, message, stack } = await errorHandler.response(
        error,
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const authResetPassword = async (payload: {
  passToken: string;
  newPassword: string;
  recaptchaToken: string;
}) => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/auth/password/reset`;
  const newPasswordHash = CryptoJS.SHA256(payload.newPassword).toString(
    CryptoJS.enc.Hex,
  );

  try {
    const response = await axios({
      method: "POST",
      url: url,
      data: omitBy(
        {
          passToken: payload.passToken,
          newPassword: newPasswordHash,
          recaptchaToken: payload.recaptchaToken,
        },
        isNil,
      ),
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, message, stack } = await errorHandler.response(
        error,
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const authLogout = async () => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/auth/logout`;

  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: url,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    localStorage.clear();
    return response.data;
  } catch (err: any) {
    authRevokeJwtToken();
    if (window) window.location.href = window.location.href; // Force refresh client browser
  }
};

export const authUpdatePassword = async ({ oldPassword, newPassword }) => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/auth/password/update`;
  const accessToken = getAccessToken();

  const oldPasswordHash = CryptoJS.SHA256(oldPassword).toString(
    CryptoJS.enc.Hex,
  );
  const newPasswordHash = CryptoJS.SHA256(newPassword).toString(
    CryptoJS.enc.Hex,
  );

  try {
    const response = await axios({
      method: "POST",
      url: url,
      data: omitBy(
        {
          oldPassword: oldPasswordHash,
          newPassword: newPasswordHash,
        },
        isNil,
      ),
      headers: {
        "Access-Control-Allow-Origin": null,
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, message, stack } = await errorHandler.response(
        error,
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const authRevokeJwtToken = () => {
  localStorage.removeItem(process.env.APP_SESSION);
};

export const authGetVerify = async (email) => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/auth/verified_email/fetch`;

  try {
    const response = await axios({
      method: "POST",
      url: url,
      data: { email: email },
      // headers: { "Access-Control-Allow-Origin": null, Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, message, stack } = await errorHandler.response(
        error,
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const authVerifyEmail = async ({
  verifyToken,
  key,
  email,
  riderType,
  subType,
  orgEmail,
}: {
  verifyToken: string;
  key?: string;
  email?: string;
  riderType?: RiderType;
  subType?: UserRoleToAssign;
  orgEmail?: string;
}) => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/auth/email/verify`;

  try {
    const response = await axios({
      method: "POST",
      url: url,
      data: omitBy(
        {
          token: verifyToken,
          id: key,
          email,
          riderType,
          subType,
          orgEmail,
        },
        (input) => isNil(input) || isEmpty(input),
      ),
    });

    return response.data.verified;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, message, stack } = await errorHandler.response(
        error,
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const authResendVerifyEmail = async ({
  email,
  pendingEmail,
}: {
  email: string;
  pendingEmail?: string;
}) => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/auth/email/verify/resend`;
  email = toLower(email);
  pendingEmail = toLower(pendingEmail);

  try {
    const response = await axios({
      method: "POST",
      url: url,
      data: {
        email: email,
        ...(pendingEmail && { pendingEmail: pendingEmail }),
        sendMail: true,
      },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const { value, code, message, stack } = await errorHandler.response(
        error,
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

const authGetOrgDetail = async (id: string, accessToken: string) => {
  const url = `${process.env.API_URL}/${process.env.API_VER}/org/fetch`;

  try {
    const response = await axios({
      method: "POST",
      url: url,
      data: { filters: { _id: id } },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    const responseData = response.data;
    return !isEmpty(responseData.org) ? responseData.org : {};
  } catch (err: any) {
    return {};
  }
};
